import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  booleanAttribute,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Translation, TranslocoService } from '@ngneat/transloco';
import { HdBodygraphActivation, HdBodygraphCenter } from '../hd-bodygraph.models';
import { HdChartHoverHighlightService } from '../hd-chart-hover-highlight.service';
import {
  HD_CENTER_NOT_SELF,
  HD_NOT_SELF_FOCUS,
} from '../hd-center-not-self-metadata';
import { buildHdCenterShapeSvg } from '../hd-center-shapes';
import { HdActivePaletteService } from '../hd-active-palette.service';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';
import { HD_BODYGRAPH_GATES_BY_CENTER } from '../hd-bodygraph-metadata';
import { HdMiniCardActivation, resolveHdMiniCardActivation } from './hd-mini-card-activation';

const P = 'humanDesign.properties';

/**
 * Reads a string array from a (possibly flattened) Transloco translation map.
 * Supports both the nested form (`prefix` resolves to an array) and the
 * flattened form (`prefix.0`, `prefix.1`, … leaf keys).
 */
function readFlattenedStringArray(translation: Translation, prefix: string): string[] {
  const direct = translation[prefix];
  if (Array.isArray(direct)) {
    return direct as string[];
  }

  const result: string[] = [];
  const indexedPrefix = `${prefix}.`;
  for (const key of Object.keys(translation)) {
    if (!key.startsWith(indexedPrefix)) continue;
    const index = Number(key.slice(indexedPrefix.length));
    if (Number.isInteger(index)) {
      result[index] = translation[key] as string;
    }
  }
  return result.filter(value => value !== undefined);
}

/**
 * Compact center card rendered in the Basis properties panel for each defined
 * and undefined center. Shows a left center-shape icon, name, themes keynote and
 * (when `variant='undefined'`) the Not-Self focus badge + keynote sentence.
 * A top color strip reserves 5px for type-color blocks (populated via `colorBlocks`).
 *
 * `variant='defined'`  → colored icon fill, no Not-Self line.
 * `variant='undefined'` → white icon with grey border, Not-Self line below.
 *
 * `interactive` maakt het kaartje een volwaardig center-element, zoals het center in
 * de chart: klik (muis, Enter, spatie) meldt `activated` (basis-paneel → center-info),
 * Ctrl/Cmd+klik togglet de freeze, en hover zet de center-hover (highlight in beide
 * richtingen via `isHighlighted`).
 */
@Component({
  selector: 'hd-center-mini-card',
  templateUrl: './hd-center-mini-card.component.html',
  styleUrl: './hd-center-mini-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  host: {
    '[class.is-interactive]': 'interactive()',
    '[class.is-chart-linked]': 'hoverLinked()',
    '[attr.role]': 'interactive() ? "button" : null',
    '[attr.tabindex]': 'interactive() ? 0 : null',
    '(click)': 'activate($event)',
    '(keydown.enter)': 'activate($event)',
    '(keydown.space)': 'activate($event)',
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()',
  },
})
export class HdCenterMiniCardComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly centerColors = inject(HdActivePaletteService);
  private readonly hoverHighlight = inject(HdChartHoverHighlightService, { optional: true });

  readonly center = input.required<HdBodygraphCenter>();
  readonly isOpen = input(false);
  /** Controls layout variant and icon coloring. Replaces the former `showNotSelfLine` input. */
  readonly variant = input<'defined' | 'undefined'>('defined');
  /** Compacte weergave voor de hoverkaart boven een center in de bodygraph. */
  readonly presentation = input<'panel' | 'hover' | 'cheat-sheet'>('panel');
  /**
   * Zet het Not-Self thema op een eigen regel i.p.v. inline vóór de eerste zin.
   * Gebruikt door de cheat-sheet-laag voor centers met een lang thema (hoofd, zelf),
   * waar een automatische wrap midden in de zin zou vallen.
   */
  readonly themeOnOwnLine = input(false);
  readonly colorBlocks = input<readonly string[]>([]);
  readonly activations = input<readonly HdBodygraphActivation[]>([]);
  /** Klikbaar kaartje (rol `button`, focusbaar); activaties komen via `activated`. */
  readonly interactive = input(false, { transform: booleanAttribute });
  /** Gekoppeld aan de chart-hover (highlight in beide richtingen + freeze); enkel samen met `interactive`. */
  readonly chartLinked = input(false, { transform: booleanAttribute });

  readonly activated = output<HdMiniCardActivation>();

  protected readonly showShapeIcon = computed(() => this.presentation() === 'panel');

  /** Doet mee aan de hover-highlight: klikbaar én gekoppeld. */
  protected readonly hoverLinked = computed(() => this.interactive() && this.chartLinked());
  private hoverPublished = false;

  /**
   * Zelfde regel als de center-highlight in de chart: het center zit in de
   * effectieve hover-state (live hover — ook vanuit dit kaartje — of bevroren).
   * Enkel wanneer het paneel aan de chart gekoppeld is.
   */
  protected readonly isHighlighted = computed(() => {
    if (this.presentation() !== 'panel' || !this.hoverLinked()) return false;
    if (!this.hoverHighlight?.enabled()) return false;
    return this.hoverHighlight.effectiveCenters().has(this.center());
  });

  protected readonly isCompletelyOpen = computed(() => {
    if (this.variant() === 'defined') return false;
    const centerGates = HD_BODYGRAPH_GATES_BY_CENTER[this.center()];
    return !this.activations().some(a => centerGates.includes(a.gate));
  });

  /** True when the Not-Self / focus line should be rendered (undefined variant only). */
  protected readonly showNotSelfLine = computed(() => this.variant() === 'undefined');

  /** De rechterhoek-focus is redundant in de compacte hoverkaart. */
  protected readonly showNotSelfHeader = computed(
    () => this.showNotSelfLine() && this.presentation() === 'panel',
  );

  /** De centerkleur van het actieve palet, als vulling van het defined-icoon. */
  protected readonly chartFillColor = computed(() => this.centerColors.color(this.center()));

  /**
   * Safe inline SVG for the center shape icon. Fill/stroke + sizing are baked
   * directly into the markup (NOT via scoped CSS): an SVG injected through
   * `[innerHTML]` does not receive the component's emulated style-encapsulation
   * attribute, so scoped `svg`/`svg *` rules never reach it. Baking guarantees a
   * coloured, correctly-sized icon regardless of encapsulation.
   *
   * - defined  → shape filled with the center's chart colour.
   * - undefined → white fill + thin grey border (non-scaling so it stays ~1.5px).
   */
  protected readonly shapeIconHtml = computed((): SafeHtml => {
    const isHighlighted = this.isHighlighted();
    // Standaard highlight-blauw uit de spec, of de gekozen hoverkleur. Als
    // inline style (niet als attribuut): een presentatie-attribuut kent geen var().
    const highlightStroke = 'style="stroke: var(--hd-hover-color, #218df0)"';

    const shapeAttrs =
      this.variant() === 'undefined'
        ? `fill="#ffffff" ${isHighlighted ? highlightStroke : 'stroke="#c9c9c9"'} stroke-width="${isHighlighted ? '2' : '1.5'}" vector-effect="non-scaling-stroke"`
        : `fill="${this.chartFillColor()}" ${isHighlighted ? `${highlightStroke} stroke-width="2" vector-effect="non-scaling-stroke"` : ''}`;

    // overflow:visible so the centered (non-scaling) border isn't clipped at the
    // viewBox edge — that clipping showed as nicked corners on undefined shapes.
    const svg = buildHdCenterShapeSvg(this.center(), shapeAttrs, 'display:block;width:auto;height:100%;overflow:visible');

    return this.sanitizer.bypassSecurityTrustHtml(svg);
  });

  /**
   * Effectieve content-taal. Leest naast `effectiveLang()` ook
   * `translationsLoadedTick()`, zodat de imperatieve `transloco.translate(key, _, lang)`
   * calls hieronder hervalideren zodra het JSON-bestand voor de nieuwe taal async
   * geladen is — anders blijven de memoized waarden op de NL-fallback hangen.
   */
  private readonly lang = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  /** Center name, with "(Open)" suffix when isOpen. */
  protected readonly baseName = computed(() => {
    const lang = this.lang();
    return this.transloco.translate(`${P}.centerNames.${this.center()}`, undefined, lang);
  });

  protected readonly openSuffix = computed(() => {
    if (!this.isOpen()) return null;
    const lang = this.lang();
    return this.transloco.translate(`${P}.openSuffix`, undefined, lang);
  });

  /** Prefix prepended to the "completely open" keynote line (e.g. "Fully open: "). */
  protected readonly fullyOpenPrefix = computed(() => {
    const lang = this.lang();
    return this.transloco.translate(`${P}.fullyOpenPrefix`, undefined, lang);
  });

  protected readonly themes = computed(() => {
    const lang = this.lang();
    return this.transloco.translate(`${P}.centerThemes.${this.center()}`, undefined, lang);
  });

  /** Dot-path base for this center's Not-Self translation node. */
  private readonly notSelfBaseKey = computed(() => `${P}.centerNotSelf.${this.center()}`);

  protected readonly notSelfTheme = computed(() => {
    const lang = this.lang();
    return this.transloco.translate<string>(`${this.notSelfBaseKey()}.theme`, undefined, lang);
  });

  protected readonly notSelfSentences = computed<string[]>(() => {
    const lang = this.lang();
    const prefix = `${this.notSelfBaseKey()}.sentences`;

    // Nested mode: the key resolves directly to the array.
    const direct = this.transloco.translate<unknown>(prefix, undefined, lang);
    if (Array.isArray(direct)) return direct as string[];

    // Flattened mode: rebuild the array from indexed leaf keys.
    const translation = this.transloco.getTranslation(lang) ?? {};
    return readFlattenedStringArray(translation, prefix);
  });

  protected readonly notSelfFirstSentence = computed(() => this.notSelfSentences()[0]);

  protected readonly notSelfRemainingSentences = computed(() => this.notSelfSentences().slice(1));

  protected readonly notSelfOpenSentence = computed(() => {
    const lang = this.lang();
    return this.transloco.translate<string>(`${this.notSelfBaseKey()}.open`, undefined, lang);
  });

  protected readonly focusShort = computed(() => {
    const focus = HD_CENTER_NOT_SELF[this.center()].focus;
    return HD_NOT_SELF_FOCUS[focus].short;
  });

  protected readonly focusKeynote = computed(() => {
    const lang = this.lang();
    const focus = HD_CENTER_NOT_SELF[this.center()].focus;
    return this.gateInfo.notSelfFocusKeynoteIn(focus, lang);
  });

  /** Hover op een gekoppeld kaartje = center-hover, zoals hover op het center in de chart. */
  protected onMouseEnter(): void {
    if (!this.hoverLinked()) return;
    this.hoverPublished = true;
    this.hoverHighlight?.setCenterHover(this.center());
  }

  protected onMouseLeave(): void {
    // Op de eigen hover-staat, niet op `hoverLinked`: wordt de koppeling tijdens een
    // hover uitgezet, dan moet de gezette center-hover toch nog opgeruimd worden.
    if (!this.hoverPublished) return;
    this.hoverPublished = false;
    this.hoverHighlight?.setCenterHover(null);
  }

  protected activate(event: Event): void {
    if (!this.interactive()) return;
    // Spatie zou anders de scrollcontainer van het paneel laten scrollen.
    if (event instanceof KeyboardEvent) event.preventDefault();
    // Ctrl/Cmd+klik = freeze-toggle, zoals op het center in de chart (enkel gekoppeld).
    if (this.hoverLinked() && event instanceof MouseEvent && (event.ctrlKey || event.metaKey) && this.hoverHighlight?.enabled()) {
      this.hoverHighlight.toggleFreezeForTarget({ kind: 'center', center: this.center() });
      return;
    }
    this.activated.emit(resolveHdMiniCardActivation(event, this.host.nativeElement));
  }
}
